import { IPaymentMethod, IPaygreenPaymentProvider, IWorldlinePaymentProvider, IShop, IPaymentProvider, IBase, PaymentProvider } from "@app/common";

export interface IDashboardPaygreenPaymentProvider extends Omit<IPaygreenPaymentProvider, 'paymentMethodsIds'> {
  shops: ({
    _id: string;
    shopId: string;
    fees: number;
  } & Pick<IShop, 'internalName' | 'publicName'>)[];

  paymentMethods: Pick<IPaymentMethod, '_id' | 'name'>[];
}

export interface IDashboardWorldlinePaymentProvider extends Omit<IWorldlinePaymentProvider, 'paymentMethodsIds'> {
  shops: ({
    _id: string;
    fees: number;
  } & Pick<IShop, 'internalName' | 'publicName'>)[];

  paymentMethods: Pick<IPaymentMethod, '_id' | 'name'>[];
}

export type IDashboardPaymentProvider = IDashboardPaygreenPaymentProvider | IDashboardWorldlinePaymentProvider;

export interface PaygreenPaymentProviderFormValues {
  type: PaymentProvider.PAYGREEN;
  shops: ({
    _id: string;
    shopId: string;
    fees: number;
  } & Pick<IShop, 'internalName' | 'publicName'>)[];
  paymentMethods: Pick<IPaymentMethod, '_id' | 'name'>[];
  rootShopId: string;
  marketplace: boolean;
  publicKey: string;
  secretKey: string;
}

export interface WorldlinePaymentProviderFormValues {
  type: PaymentProvider.WORLDLINE;
  shops: ({
    _id: string;
    fees: number;
  } & Pick<IShop, 'internalName' | 'publicName'>)[];
  paymentMethods: Pick<IPaymentMethod, '_id' | 'name'>[];
  merchantId: string;
  apiKey: string;
  apiSecret: string;
}

export type PaymentProviderFormValues = PaygreenPaymentProviderFormValues | WorldlinePaymentProviderFormValues;

export const transformPaymentProviderFormValuesToPayload = (values: PaymentProviderFormValues): Omit<IPaymentProvider, keyof IBase> => {
  const basePayload = {
    type: values.type,
    paymentMethodsIds: values.paymentMethods.map((paymentMethod) => paymentMethod._id),
  };

  if (values.type === PaymentProvider.PAYGREEN) {
    return {
      ...basePayload,
      type: PaymentProvider.PAYGREEN,
      shops: (values.shops || []).map((shop) => ({
        _id: shop._id,
        shopId: shop.shopId,
        fees: shop.fees ?? 0,
      })),
      marketplace: Boolean(values.marketplace),
      rootShopId: values.rootShopId,
      publicKey: values.publicKey,
      secretKey: values.secretKey,
    };
  }

  // Worldline
  return {
    ...basePayload,
    type: PaymentProvider.WORLDLINE,
    shops: (values.shops || []).map((shop) => ({
      _id: shop._id,
      fees: shop.fees ?? 0,
    })),
    merchantId: values.merchantId,
    apiKey: values.apiKey,
    apiSecret: values.apiSecret,
  };
}

export const getDefaultPaygreenFormValues = (): PaygreenPaymentProviderFormValues => ({
  type: PaymentProvider.PAYGREEN,
  shops: [],
  marketplace: true,
  paymentMethods: [],
  rootShopId: '',
  publicKey: '',
  secretKey: '',
});

export const getDefaultWorldlineFormValues = (): WorldlinePaymentProviderFormValues => ({
  type: PaymentProvider.WORLDLINE,
  shops: [],
  paymentMethods: [],
  merchantId: '',
  apiKey: '',
  apiSecret: '',
});

export const getDefaultFormValues = (type: PaymentProvider = PaymentProvider.PAYGREEN): PaymentProviderFormValues => {
  if (type === PaymentProvider.WORLDLINE) {
    return getDefaultWorldlineFormValues();
  }
  return getDefaultPaygreenFormValues();
};

export const transformToUpdatableFormPaymentProvider = (paymentProvider?: Partial<IDashboardPaymentProvider>): PaymentProviderFormValues => {
  if (!paymentProvider?.type || paymentProvider.type === PaymentProvider.PAYGREEN) {
    const provider = paymentProvider as Partial<IDashboardPaygreenPaymentProvider> | undefined;
    return {
      type: PaymentProvider.PAYGREEN,
      shops: (provider?.shops || []).map((shop) => ({
        _id: shop._id,
        shopId: shop.shopId,
        fees: shop.fees ?? 0,
        internalName: shop.internalName,
        publicName: shop.publicName,
      })),
      marketplace: Boolean(provider?.marketplace),
      paymentMethods: provider?.paymentMethods || [],
      rootShopId: provider?.rootShopId || '',
      publicKey: provider?.publicKey || '',
      secretKey: provider?.secretKey || '',
    };
  }

  // Worldline
  const provider = paymentProvider as Partial<IDashboardWorldlinePaymentProvider>;
  return {
    type: PaymentProvider.WORLDLINE,
    shops: (provider?.shops || []).map((shop) => ({
      _id: shop._id,
      fees: shop.fees ?? 0,
      internalName: shop.internalName,
      publicName: shop.publicName,
    })),
    paymentMethods: provider?.paymentMethods || [],
    merchantId: provider?.merchantId || '',
    apiKey: provider?.apiKey || '',
    apiSecret: provider?.apiSecret || '',
  };
}
